import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Wallet } from "lucide-react";

// Simplified but realistic 2026-27 budgeting model. All figures in euros.
const CITIES = [
  { id: "paris", label: "Paris / Île-de-France", multiplier: 1.4, rentBase: { crous: 480, studio: 1100, colocation: 700 }, transport: 84, food: 380 },
  { id: "lyon", label: "Lyon", multiplier: 1.1, rentBase: { crous: 330, studio: 700, colocation: 480 }, transport: 32, food: 340 },
  { id: "bordeaux", label: "Bordeaux", multiplier: 1.1, rentBase: { crous: 320, studio: 680, colocation: 460 }, transport: 30, food: 340 },
  { id: "toulouse", label: "Toulouse", multiplier: 1.05, rentBase: { crous: 310, studio: 620, colocation: 430 }, transport: 25, food: 320 },
  { id: "nice", label: "Nice / Côte d'Azur", multiplier: 1.15, rentBase: { crous: 360, studio: 780, colocation: 520 }, transport: 30, food: 350 },
  { id: "marseille", label: "Marseille", multiplier: 1.0, rentBase: { crous: 300, studio: 620, colocation: 420 }, transport: 25, food: 320 },
  { id: "strasbourg", label: "Strasbourg", multiplier: 1.0, rentBase: { crous: 300, studio: 600, colocation: 400 }, transport: 26, food: 320 },
  { id: "montpellier", label: "Montpellier", multiplier: 1.0, rentBase: { crous: 290, studio: 580, colocation: 400 }, transport: 25, food: 310 },
  { id: "nantes", label: "Nantes", multiplier: 1.0, rentBase: { crous: 300, studio: 590, colocation: 410 }, transport: 27, food: 315 },
  { id: "grenoble", label: "Grenoble", multiplier: 1.0, rentBase: { crous: 300, studio: 590, colocation: 410 }, transport: 25, food: 315 },
  { id: "rennes", label: "Rennes", multiplier: 0.95, rentBase: { crous: 285, studio: 560, colocation: 390 }, transport: 27, food: 305 },
  { id: "lille", label: "Lille", multiplier: 1.0, rentBase: { crous: 295, studio: 580, colocation: 400 }, transport: 32, food: 315 },
  { id: "aix", label: "Aix-en-Provence", multiplier: 1.1, rentBase: { crous: 340, studio: 720, colocation: 480 }, transport: 26, food: 335 },
];

const PROGRAMS = [
  { id: "public-bachelor", label: "Public bachelor's", tuition: 2895 },
  { id: "public-master", label: "Public master's", tuition: 3941 },
  { id: "grande-ecole-bachelor", label: "Grande école bachelor's", tuition: 12500 },
  { id: "grande-ecole-master", label: "Grande école master's", tuition: 18500 },
  { id: "business-mba", label: "Top business school MBA", tuition: 65000 },
];

const HOUSING = [
  { id: "crous", label: "CROUS student residence" },
  { id: "colocation", label: "Colocation (shared flat)" },
  { id: "studio", label: "Private studio" },
];

const LIFESTYLE = [
  { id: "frugal", label: "Frugal", factor: 0.85 },
  { id: "balanced", label: "Balanced", factor: 1.0 },
  { id: "comfortable", label: "Comfortable", factor: 1.2 },
];

// CAF monthly subsidy estimate by housing type (very rough — depends on lease and revenue)
const CAF = { crous: 130, colocation: 170, studio: 210 };

const eur = (n) => `€${Math.round(n).toLocaleString()}`;
const usd = (eur) => `$${Math.round(eur * 1.17).toLocaleString()}`;

export default function Calculator() {
  const [cityId, setCityId] = useState("lyon");
  const [programId, setProgramId] = useState("public-bachelor");
  const [housingId, setHousingId] = useState("crous");
  const [lifestyleId, setLifestyleId] = useState("balanced");

  const city = CITIES.find((c) => c.id === cityId);
  const program = PROGRAMS.find((p) => p.id === programId);
  const lifestyle = LIFESTYLE.find((l) => l.id === lifestyleId);

  const breakdown = useMemo(() => {
    const rent = city.rentBase[housingId] * 12;
    const food = city.food * 12 * lifestyle.factor;
    const transport = city.transport * 12;
    const health = 50 * 12; // mutuelle top-up
    const misc = 100 * 12 * lifestyle.factor; // phone, admin, small extras
    const cafRebate = CAF[housingId] * 12;

    const livingBeforeAid = rent + food + transport + health + misc;
    const livingNet = Math.max(livingBeforeAid - cafRebate, 0);
    const total = program.tuition + livingNet;

    return {
      tuition: program.tuition,
      rent,
      food,
      transport,
      health,
      misc,
      cafRebate,
      livingBeforeAid,
      livingNet,
      total,
    };
  }, [city, program, housingId, lifestyle]);

  const usAvg = 62570; // US private nonprofit avg cost of attendance
  const savings = Math.max(usAvg - breakdown.total * 1.17, 0);

  return (
    <div className="pt-28 md:pt-32 pb-24 min-h-screen" data-testid="page-calculator">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="rule-gold" /><span className="eyebrow">Cost calculator · Free</span>
        </div>
        <div className="grid md:grid-cols-12 gap-8 items-end">
          <h1 className="md:col-span-8 serif text-5xl md:text-7xl leading-[0.95] tracking-tight">
            Calculate your <em className="italic text-[#b8892b]">real year one</em>.
          </h1>
          <p className="md:col-span-4 text-lg text-[#4a5570] leading-relaxed">
            Pick a city, program, housing type, and lifestyle. We estimate tuition, rent, food, transit, health, plus CAF subsidy.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          {/* Inputs */}
          <div className="lg:col-span-5 bg-white border border-[#0d1b3e]/10 p-6 md:p-8 lg:sticky lg:top-28 self-start" data-testid="calculator-inputs">
            <Field label="City / region">
              <select value={cityId} onChange={(e) => setCityId(e.target.value)}
                data-testid="calc-city"
                className="w-full border border-[#0d1b3e]/20 bg-white px-3 py-2 outline-none focus:border-[#b8892b]">
                {CITIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Program type">
              <select value={programId} onChange={(e) => setProgramId(e.target.value)}
                data-testid="calc-program"
                className="w-full border border-[#0d1b3e]/20 bg-white px-3 py-2 outline-none focus:border-[#b8892b]">
                {PROGRAMS.map((p) => <option key={p.id} value={p.id}>{p.label} · {eur(p.tuition)}/yr</option>)}
              </select>
            </Field>
            <Field label="Housing type">
              <div className="grid grid-cols-3 gap-2">
                {HOUSING.map((h) => (
                  <button key={h.id} onClick={() => setHousingId(h.id)}
                    data-testid={`calc-housing-${h.id}`}
                    className={`text-xs uppercase tracking-[0.14em] px-2 py-3 border transition-colors ${housingId === h.id ? "bg-[#0d1b3e] text-white border-[#0d1b3e]" : "border-[#0d1b3e]/20 text-[#0d1b3e] hover:border-[#b8892b]"}`}>
                    {h.label}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Lifestyle">
              <div className="grid grid-cols-3 gap-2">
                {LIFESTYLE.map((l) => (
                  <button key={l.id} onClick={() => setLifestyleId(l.id)}
                    data-testid={`calc-lifestyle-${l.id}`}
                    className={`text-xs uppercase tracking-[0.14em] px-2 py-3 border transition-colors ${lifestyleId === l.id ? "bg-[#0d1b3e] text-white border-[#0d1b3e]" : "border-[#0d1b3e]/20 text-[#0d1b3e] hover:border-[#b8892b]"}`}>
                    {l.label}
                  </button>
                ))}
              </div>
            </Field>
          </div>

          {/* Results */}
          <motion.div layout className="lg:col-span-7 space-y-6" data-testid="calculator-results">
            <div className="bg-[#0d1b3e] text-white p-8">
              <div className="flex items-center gap-2 text-[#b8892b] mb-2">
                <Wallet size={16} /><span className="eyebrow !text-[#b8892b]">Your estimated year 1</span>
              </div>
              <div className="serif text-6xl md:text-7xl text-white mt-1" data-testid="calc-total-eur">{eur(breakdown.total)}</div>
              <div className="text-white/60 text-lg mt-1" data-testid="calc-total-usd">≈ {usd(breakdown.total)} · tuition + net cost of living</div>

              <div className="mt-6 grid grid-cols-2 gap-6 border-t border-white/10 pt-6">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/60">vs. US private avg</div>
                  <div className="serif text-3xl text-[#b8892b] mt-1">Save ${Math.round(savings).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/60">CAF subsidy applied</div>
                  <div className="serif text-3xl text-[#b8892b] mt-1">{eur(breakdown.cafRebate)}/yr</div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-[#0d1b3e]/10 p-6">
              <div className="eyebrow mb-4">Detailed breakdown (annual)</div>
              {[
                ["Tuition", breakdown.tuition],
                ["Rent", breakdown.rent],
                ["Food & groceries", breakdown.food],
                ["Transport", breakdown.transport],
                ["Health (mutuelle)", breakdown.health],
                ["Phone, admin, misc.", breakdown.misc],
                ["CAF subsidy", -breakdown.cafRebate, true],
              ].map(([label, amount, neg], i) => (
                <div key={i} className="flex justify-between border-b border-[#0d1b3e]/10 py-3 text-[#0d1b3e]">
                  <span>{label}</span>
                  <span className={neg ? "text-[#b8892b]" : ""}>{eur(amount)}</span>
                </div>
              ))}
              <div className="flex justify-between pt-4 text-[#0d1b3e]">
                <span className="serif text-xl">Total year 1</span>
                <span className="serif text-xl text-[#b8892b]">{eur(breakdown.total)}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Link to="/advisor" className="btn-sharp btn-sharp-gold" data-testid="calc-cta-advisor">
                Get my free study plan <ArrowRight size={16} />
              </Link>
              <Link to="/pricing" className="btn-sharp btn-sharp-outline" data-testid="calc-cta-pricing">
                Talk to Rose
              </Link>
            </div>

            <p className="text-xs text-[#4a5570] italic max-w-2xl leading-relaxed">
              Estimates based on published 2026, 27 tuition rates and typical student cost-of-living data. Actual costs vary by university, program, and lifestyle. Currency conversion at 1 EUR = 1.17 USD.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-5">
      <label className="text-[10px] uppercase tracking-[0.22em] text-[#4a5570] block mb-2">{label}</label>
      {children}
    </div>
  );
}
